import { radToDeg } from './transforms';
import type { TestChartData } from './types';
import type { ReferenceLabelDto } from '@/types/referenceLabels';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(): string {
  return new Date().toISOString().slice(0, 10); // YYYY-MM-DD
}

/** Escapes a value for safe inclusion in a CSV cell. */
function escCsv(val: string | null | undefined): string {
  if (val == null) return '';
  const s = String(val);
  if (s.includes(',') || s.includes('"') || s.includes('\n')) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

// ---------------------------------------------------------------------------
// File download
// ---------------------------------------------------------------------------

export function downloadFile(content: string, filename: string, mimeType: string): void {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function getExportFilename(
  chartType: 'bode' | 'nyquist' | 'delta' | 'combined',
  ext: string,
  labeled?: boolean,
): string {
  const labeledSuffix = labeled ? '-labeled' : '';
  return `labcloud-analysis-${chartType}${labeledSuffix}-${formatDate()}.${ext}`;
}

export function getPerTestExportFilename(view: string, testLabel: string, ext: string): string {
  const sanitised = (testLabel
    .replace(/[^a-zA-Z0-9_-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80)) || 'untitled';
  return `labcloud-${view}-${sanitised}-${formatDate()}.${ext}`;
}

// ---------------------------------------------------------------------------
// CSV export
// ---------------------------------------------------------------------------

/**
 * Generates a CSV string for the given chart data.
 *
 * - bode:    test_id, source_baseline, source_specimen, notes, channel, frequency_Hz,
 *            magnitude_Ohm, phase_rad, phase_deg, measurement_type
 * - nyquist: …same + Z_real, Z_imag
 * - delta:   test_id, source_baseline, source_specimen, notes, channel, frequency_Hz,
 *            delta_magnitude_Ohm, delta_phase_rad, delta_Z_real, delta_Z_imag
 *
 * For delta mode the `testData` must already contain pre-computed delta values
 * in the `baseline` field (as produced by buildDeltaTestData in the page).
 */
export function exportToCsv(
  testData: TestChartData[],
  mode: 'bode' | 'nyquist' | 'delta' | 'combined',
  visibleChannels: Set<string>,
  viewMode: 'baselines' | 'specimens' | 'both',
  referenceLabels?: Map<string, Map<number, ReferenceLabelDto>>,
): string {
  const now = new Date().toISOString();
  const channelList = Array.from(visibleChannels).sort().join(', ');

  const metaLines = [
    '# Ediphor LabCloud — Measurement Analysis Export',
    `# Exported: ${now}`,
    `# Chart type: ${mode}`,
    `# Tests: ${testData.length}`,
    `# Channels: ${channelList}`,
    ...(mode !== 'delta' ? [`# View mode: ${viewMode}`] : []),
    ...(referenceLabels !== undefined ? ['# Reference labels: included'] : []),
  ].join('\n');

  const labelColHeader = referenceLabels !== undefined ? ',reference_label,reference_label_notes,specimen_concentration_cpm' : '';

  function getLabelCols(testId: string, channelId: string): string[] {
    if (referenceLabels === undefined) return [];
    const chNum = parseInt(channelId.replace('CH', ''), 10);
    const dto = referenceLabels.get(testId)?.get(chNum) ?? null;
    const label = dto?.referenceLabel != null ? dto.referenceLabel : '';
    const notes = dto?.notes != null ? escCsv(dto.notes) : '';
    const concentration = dto?.concentration != null ? String(dto.concentration) : '';
    return [label, notes, concentration];
  }

  const rows: string[] = [];

  if (mode === 'delta') {
    const header =
      'test_id,source_baseline,source_specimen,notes,channel,frequency_Hz,' +
      `delta_magnitude_Ohm,delta_phase_rad,delta_Z_real,delta_Z_imag${labelColHeader}`;
    rows.push(header);

    for (const test of testData) {
      if (!test.baseline) continue;
      for (const ch of test.baseline.channels) {
        if (!visibleChannels.has(ch.channelId)) continue;
        for (const pt of ch.points) {
          rows.push(
            [
              escCsv(test.testId),
              escCsv(test.sourceBaseline),
              escCsv(test.sourceSpecimen),
              escCsv(test.notes),
              ch.channelId,
              pt.frequency,
              pt.magnitude,
              pt.phase,
              pt.zReal,
              pt.zImag,
              ...getLabelCols(test.testId, ch.channelId),
            ].join(','),
          );
        }
      }
    }
  } else if (mode === 'nyquist') {
    const header =
      'test_id,source_baseline,source_specimen,notes,channel,frequency_Hz,' +
      `magnitude_Ohm,phase_rad,Z_real,Z_imag,measurement_type${labelColHeader}`;
    rows.push(header);

    const mtypes =
      viewMode === 'baselines'
        ? (['baseline'] as const)
        : viewMode === 'specimens'
          ? (['specimen'] as const)
          : (['baseline', 'specimen'] as const);

    for (const test of testData) {
      for (const mtype of mtypes) {
        const md = test[mtype];
        if (!md) continue;
        for (const ch of md.channels) {
          if (!visibleChannels.has(ch.channelId)) continue;
          for (const pt of ch.points) {
            rows.push(
              [
                escCsv(test.testId),
                escCsv(test.sourceBaseline),
                escCsv(test.sourceSpecimen),
                escCsv(test.notes),
                ch.channelId,
                pt.frequency,
                pt.magnitude,
                pt.phase,
                pt.zReal,
                pt.zImag,
                mtype,
                ...getLabelCols(test.testId, ch.channelId),
              ].join(','),
            );
          }
        }
      }
    }
  } else {
    // bode
    const header =
      'test_id,source_baseline,source_specimen,notes,channel,frequency_Hz,' +
      `magnitude_Ohm,phase_rad,phase_deg,measurement_type${labelColHeader}`;
    rows.push(header);

    const mtypes =
      viewMode === 'baselines'
        ? (['baseline'] as const)
        : viewMode === 'specimens'
          ? (['specimen'] as const)
          : (['baseline', 'specimen'] as const);

    for (const test of testData) {
      for (const mtype of mtypes) {
        const md = test[mtype];
        if (!md) continue;
        for (const ch of md.channels) {
          if (!visibleChannels.has(ch.channelId)) continue;
          for (const pt of ch.points) {
            rows.push(
              [
                escCsv(test.testId),
                escCsv(test.sourceBaseline),
                escCsv(test.sourceSpecimen),
                escCsv(test.notes),
                ch.channelId,
                pt.frequency,
                pt.magnitude,
                pt.phase,
                radToDeg(pt.phase),
                mtype,
                ...getLabelCols(test.testId, ch.channelId),
              ].join(','),
            );
          }
        }
      }
    }
  }

  return `${metaLines}\n${rows.join('\n')}`;
}

// ---------------------------------------------------------------------------
// JSON export
// ---------------------------------------------------------------------------

/**
 * Generates a structured JSON string for the given chart data.
 * Delta mode exposes data under a "delta" key instead of "baseline"/"specimen".
 */
export function exportToJson(
  testData: TestChartData[],
  mode: 'bode' | 'nyquist' | 'delta' | 'combined',
  visibleChannels: Set<string>,
  viewMode: 'baselines' | 'specimens' | 'both',
  referenceLabels?: Map<string, Map<number, ReferenceLabelDto>>,
): string {
  const mtypes: ReadonlyArray<'baseline' | 'specimen'> =
    mode === 'delta'
      ? ['baseline']
      : viewMode === 'baselines'
        ? ['baseline']
        : viewMode === 'specimens'
          ? ['specimen']
          : ['baseline', 'specimen'];

  const output = {
    metadata: {
      exportedAt: new Date().toISOString(),
      chartType: mode,
      testCount: testData.length,
      channels: Array.from(visibleChannels).sort(),
      ...(mode !== 'delta' ? { viewMode } : {}),
      ...(referenceLabels !== undefined ? { referenceLabelExport: true } : {}),
    },
    tests: testData.map((test) => {
      const entry: Record<string, unknown> = {
        testId: test.testId,
        sourceBaseline: test.sourceBaseline ?? null,
        sourceSpecimen: test.sourceSpecimen ?? null,
        notes: test.notes ?? null,
      };

      for (const mtype of mtypes) {
        const md = test[mtype];
        // For delta mode, expose the data under the key "delta"
        const outputKey = mode === 'delta' ? 'delta' : mtype;

        if (!md) {
          entry[outputKey] = null;
          continue;
        }

        entry[outputKey] = {
          channels: md.channels
            .filter((ch) => visibleChannels.has(ch.channelId))
            .map((ch) => {
              const chNum = parseInt(ch.channelId.replace('CH', ''), 10);
              const dto = referenceLabels?.get(test.testId)?.get(chNum) ?? null;
              return {
                channelId: ch.channelId,
                ...(referenceLabels !== undefined
                  ? {
                      referenceLabel: dto?.referenceLabel ?? null,
                      referenceLabelNotes: dto?.notes ?? null,
                      specimenConcentration: dto?.concentration ?? null,
                    }
                  : {}),
                points: ch.points.map((pt) => ({
                  frequency: pt.frequency,
                  magnitude: pt.magnitude,
                  phase_rad: pt.phase,
                  phase_deg: radToDeg(pt.phase),
                  zReal: pt.zReal,
                  zImag: pt.zImag,
                })),
              };
            }),
        };
      }

      return entry;
    }),
  };

  return JSON.stringify(output, null, 2);
}
